import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BuildingOfficeIcon,
  GlobeAltIcon,
  IdentificationIcon,
  PhoneIcon,
  UserCircleIcon,
} from "@heroicons/react/20/solid";
import FormInput from "../../../components/controls/FormInput";
import Autocomplete from "../../../components/Places/PlacesAutocomplete";
import ImageUploader from "../../../components/controls/ImageUploader";
import { addOrUpdateCompany } from "../../../db/Companies";

export default function AdminCompanyEdit({ isOpen, onClose, company, onSave }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (company) {
      setForm({
        ...company,
        tempImageUrl: company.imageUrl || "",
        hasChangedImageUrl: false,
        isActive: company.isActive ?? true,
      });
    }
  }, [company]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      if (field === "location") {
        return {
          ...prev,
          location: { ...prev.location, ...value },
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      imageUrl: form.hasChangedImageUrl
        ? form.tempImageUrl ?? null
        : form.imageUrl,
    };

    try {
      const updated = await addOrUpdateCompany(payload);
      onSave({ ...payload, id: updated }); // si tu retournes l’ID depuis l’API
    } catch (err) {
      console.error(err);
      alert(t("errors.submission"));
    }
  };

  if (!isOpen || !form) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 overflow-y-auto text-primary">
      <div className="min-h-screen flex flex-col items-center justify-start py-4 px-4">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl p-6 relative">
          <button
            onClick={onClose}
            className="btn btn-sm btn-circle absolute top-2 right-2"
          >
            ✕
          </button>

          <h2 className="text-2xl font-bold mb-4 text-center">
            {t("adminUserTable.companyManager")}
          </h2>

          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 sm:grid-cols-2 gap-2 border p-4 rounded-lg"
          >
            <FormInput
              label={t("becomeProvider.companyNameLabel")}
              value={form.name}
              placeholder={t("becomeProvider.companyNamePlaceholder")}
              onChange={(name, value) => handleChange("name", value)}
              required
              icon={<BuildingOfficeIcon className="w-5 h-5 text-gray-500" />}
            />
            <Autocomplete
              onSelect={(e) => {
                handleChange("location", e);
                handleChange("address", e.formattedAddress);
              }}
              initialAddress={form.address || ""}
            />
            <FormInput
              label={t("becomeProvider.contactEmailLabel")}
              value={form.contactEmail || ""}
              placeholder={t("becomeProvider.contactEmailPlaceholder")}
              onChange={(name, value) => handleChange("contactEmail", value)}
              required
            />
            <FormInput
              label={t("becomeProvider.phoneNumberLabel")}
              value={form.phoneNumber}
              placeholder={t("becomeProvider.phoneNumberPlaceholder")}
              onChange={(name, value) => handleChange("phoneNumber", value)}
              icon={<PhoneIcon className="w-5 h-5 text-gray-500" />}
              required
            />
            <FormInput
              label={t("becomeProvider.contactPersonLabel")}
              value={form.contactPerson}
              placeholder={t("becomeProvider.contactPersonPlaceholder")}
              onChange={(name, value) => handleChange("contactPerson", value)}
              icon={<UserCircleIcon className="w-5 h-5 text-gray-500" />}
              required
            />
            <FormInput
              label={t("becomeProvider.neqNumberLabel")}
              value={form.neqNumber}
              placeholder={t("becomeProvider.neqNumberPlaceholder")}
              onChange={(name, value) => handleChange("neqNumber", value)}
              icon={<IdentificationIcon className="w-5 h-5 text-gray-500" />}
            />
            <FormInput
              label={t("becomeProvider.websiteLabel")}
              value={form.website}
              placeholder={t("becomeProvider.websitePlaceholder")}
              onChange={(name, value) => handleChange("website", value)}
              icon={<GlobeAltIcon className="w-5 h-5 text-gray-500" />}
            />
            <div className="form-control col-span-2">
              <label className="cursor-pointer label flex items-center gap-4">
                <span
                  className={`label-text font-semibold ${
                    form.isActive ? "text-green-600" : "text-red-500"
                  }`}
                >
                  {form.isActive
                    ? t("adminUserTable.companyActive")
                    : t("adminUserTable.companyInactive")}
                </span>
                <input
                  type="checkbox"
                  className={`toggle ${
                    form.isActive ? "toggle-success" : "toggle-error"
                  }`}
                  checked={form.isActive}
                  onChange={(e) => {
                    const confirmed = !e.target.checked
                      ? window.confirm(t("adminUserTable.confirmDeactivate"))
                      : true;
                    if (!confirmed) return;
                    setForm((prev) => ({
                      ...prev,
                      isActive: e.target.checked,
                    }));
                  }}
                />
              </label>
            </div>

            <div className="col-span-2">
              <ImageUploader
                initialImageUrl={form.tempImageUrl || ""}
                onTempImageChange={(url) => {
                  setForm((prev) => ({
                    ...prev,
                    tempImageUrl: url?.trim() || "",
                    hasChangedImageUrl: true,
                  }));
                }}
              />
            </div>

            <div className="sm:col-span-2 flex justify-end mt-4 border-t">
              <button
                type="submit"
                className="p-4 rounded-full bg-primary text-white hover:bg-accent mt-2"
              >
                {t("modal.save")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
